import { useState, useEffect } from "react";
import axios from "axios";
import { 
  GraduationCap, 
  CheckCircle, 
  XCircle,
  Warning,
  Prohibit,
  Buildings,
  Users,
  Lock,
  Cpu,
  ChartLine,
  CaretDown,
  CaretRight,
  FileText,
  PencilSimple,
  X,
  FloppyDisk,
  Info,
  Export,
  Target
} from "@phosphor-icons/react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Progress } from "@/components/ui/progress";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Structure ISO 27001:2022 complète
const ISO_STRUCTURE = [
  {
    id: "clause4",
    title: "4. Contexte de l'organisation",
    icon: Buildings,
    type: "requirement",
    items: [
      { id: "4.1", title: "Compréhension de l'organisation et de son contexte", description: "Déterminer les enjeux externes et internes pertinents", excludable: false },
      { id: "4.2", title: "Compréhension des besoins et attentes des parties intéressées", description: "Identifier les parties prenantes et leurs exigences", excludable: false },
      { id: "4.3", title: "Détermination du domaine d'application du SMSI", description: "Définir les limites et l'applicabilité", excludable: false },
      { id: "4.4", title: "Système de management de la sécurité de l'information", description: "Établir, mettre en œuvre, tenir à jour et améliorer", excludable: false },
    ]
  },
  {
    id: "clause5",
    title: "5. Leadership",
    icon: Users,
    type: "requirement",
    items: [
      { id: "5.1", title: "Leadership et engagement", description: "Engagement de la direction", excludable: false },
      { id: "5.2", title: "Politique", description: "Politique de sécurité de l'information", excludable: false },
      { id: "5.3", title: "Rôles, responsabilités et autorités au sein de l'organisation", description: "Attribution des rôles et responsabilités", excludable: false },
    ]
  },
  {
    id: "clause6",
    title: "6. Planification",
    icon: ChartLine,
    type: "requirement",
    items: [
      { id: "6.1", title: "Actions à mettre en œuvre face aux risques et opportunités", description: "Appréciation et traitement des risques", excludable: false },
      { id: "6.2", title: "Objectifs de sécurité de l'information et plans pour les atteindre", description: "Objectifs mesurables", excludable: false },
      { id: "6.3", title: "Planification des modifications", description: "Gestion des changements", excludable: false },
    ]
  },
  {
    id: "clause7",
    title: "7. Support",
    icon: FileText,
    type: "requirement",
    items: [
      { id: "7.1", title: "Ressources", description: "Ressources nécessaires", excludable: false },
      { id: "7.2", title: "Compétences", description: "Compétences du personnel", excludable: false },
      { id: "7.3", title: "Sensibilisation", description: "Sensibilisation du personnel", excludable: false },
      { id: "7.4", title: "Communication", description: "Communications internes et externes", excludable: false },
      { id: "7.5", title: "Informations documentées", description: "Maîtrise documentaire", excludable: false },
    ]
  },
  {
    id: "clause8",
    title: "8. Fonctionnement",
    icon: Cpu,
    type: "requirement",
    items: [
      { id: "8.1", title: "Planification et maîtrise opérationnelles", description: "Planification et contrôle des processus", excludable: false },
      { id: "8.2", title: "Appréciation des risques de sécurité de l'information", description: "Réalisation des appréciations", excludable: false },
      { id: "8.3", title: "Traitement des risques de sécurité de l'information", description: "Mise en œuvre du plan de traitement", excludable: false },
    ]
  },
  {
    id: "clause9",
    title: "9. Évaluation des performances",
    icon: ChartLine,
    type: "requirement",
    items: [
      { id: "9.1", title: "Surveillance, mesure, analyse et évaluation", description: "Indicateurs de performance", excludable: false },
      { id: "9.2", title: "Audit interne", description: "Programme d'audit", excludable: false },
      { id: "9.3", title: "Revue de direction", description: "Revues périodiques", excludable: false },
    ]
  },
  {
    id: "clause10",
    title: "10. Amélioration",
    icon: Target,
    type: "requirement",
    items: [
      { id: "10.1", title: "Non-conformité et actions correctives", description: "Gestion des non-conformités", excludable: false },
      { id: "10.2", title: "Amélioration continue", description: "Amélioration du SMSI", excludable: false },
    ]
  },
  {
    id: "annexA5",
    title: "A.5 - Mesures organisationnelles",
    icon: Buildings,
    type: "control",
    items: [
      { id: "A.5.1", title: "Politiques de sécurité de l'information", description: "Politiques approuvées par la direction", excludable: true },
      { id: "A.5.2", title: "Fonctions et responsabilités liées à la sécurité de l'information", description: "Définition et attribution des responsabilités", excludable: true },
      { id: "A.5.3", title: "Séparation des tâches", description: "Tâches et domaines de responsabilité incompatibles séparés", excludable: true },
      { id: "A.5.4", title: "Responsabilités de la direction", description: "La direction exige l'application de la politique", excludable: true },
      { id: "A.5.5", title: "Relations avec les autorités", description: "Contacts avec les autorités compétentes", excludable: true },
      { id: "A.5.6", title: "Relations avec des groupes de travail spécialisés", description: "Contacts avec groupes d'intérêt spécialisés", excludable: true },
      { id: "A.5.7", title: "Renseignements sur les menaces", description: "Collecte et analyse des menaces", excludable: true },
      { id: "A.5.8", title: "Sécurité de l'information dans la gestion de projet", description: "Intégration dans la gestion de projet", excludable: true },
      { id: "A.5.9", title: "Inventaire des informations et autres actifs associés", description: "Inventaire tenu à jour", excludable: true },
      { id: "A.5.10", title: "Utilisation correcte des informations et autres actifs associés", description: "Règles d'utilisation identifiées et documentées", excludable: true },
      { id: "A.5.11", title: "Restitution des actifs", description: "Restitution à la fin du contrat", excludable: true },
      { id: "A.5.12", title: "Classification des informations", description: "Schéma de classification", excludable: true },
      { id: "A.5.13", title: "Marquage des informations", description: "Procédures de marquage", excludable: true },
      { id: "A.5.14", title: "Transfert des informations", description: "Règles et procédures de transfert", excludable: true },
      { id: "A.5.15", title: "Contrôle d'accès", description: "Règles de contrôle d'accès", excludable: true },
      { id: "A.5.16", title: "Gestion des identités", description: "Cycle de vie complet des identités", excludable: true },
      { id: "A.5.17", title: "Informations d'authentification", description: "Attribution et gestion contrôlées", excludable: true },
      { id: "A.5.18", title: "Droits d'accès", description: "Gestion selon la politique", excludable: true },
      { id: "A.5.19", title: "Sécurité de l'information dans les relations avec les fournisseurs", description: "Exigences pour les fournisseurs", excludable: true },
      { id: "A.5.20", title: "Prise en compte de la sécurité dans les accords avec les fournisseurs", description: "Exigences contractuelles", excludable: true },
      { id: "A.5.21", title: "Gestion de la sécurité de l'information dans la chaîne TIC", description: "Gestion des risques fournisseurs TIC", excludable: true },
      { id: "A.5.22", title: "Surveillance, révision et gestion des changements des services fournisseurs", description: "Surveillance des fournisseurs", excludable: true },
      { id: "A.5.23", title: "Sécurité de l'information dans l'utilisation de services en nuage", description: "Acquisition et gestion des services cloud", excludable: true },
      { id: "A.5.24", title: "Planification et préparation de la gestion des incidents", description: "Processus de gestion des incidents", excludable: true },
      { id: "A.5.25", title: "Appréciation des événements liés à la sécurité et prise de décision", description: "Évaluation et classification", excludable: true },
      { id: "A.5.26", title: "Réponse aux incidents liés à la sécurité de l'information", description: "Procédures de réponse", excludable: true },
      { id: "A.5.27", title: "Tirer des enseignements des incidents", description: "Amélioration continue", excludable: true },
      { id: "A.5.28", title: "Collecte de preuves", description: "Procédures de collecte de preuves", excludable: true },
      { id: "A.5.29", title: "Sécurité de l'information pendant une perturbation", description: "Continuité de la sécurité", excludable: true },
      { id: "A.5.30", title: "Préparation des TIC pour la continuité d'activité", description: "Disponibilité des TIC", excludable: true },
      { id: "A.5.31", title: "Exigences légales, statutaires, réglementaires et contractuelles", description: "Identification des exigences", excludable: true },
      { id: "A.5.32", title: "Droits de propriété intellectuelle", description: "Protection de la propriété intellectuelle", excludable: true },
      { id: "A.5.33", title: "Protection des enregistrements", description: "Protection contre la perte et falsification", excludable: true },
      { id: "A.5.34", title: "Protection de la vie privée et des DCP", description: "Conformité vie privée", excludable: true },
      { id: "A.5.35", title: "Revue indépendante de la sécurité de l'information", description: "Revues indépendantes", excludable: true },
      { id: "A.5.36", title: "Conformité aux politiques et normes de sécurité", description: "Revues de conformité", excludable: true },
      { id: "A.5.37", title: "Procédures d'exploitation documentées", description: "Documentation des procédures", excludable: true },
    ]
  },
  {
    id: "annexA6",
    title: "A.6 - Mesures liées aux personnes",
    icon: Users,
    type: "control",
    items: [
      { id: "A.6.1", title: "Sélection des candidats", description: "Vérification des antécédents", excludable: true },
      { id: "A.6.2", title: "Termes et conditions d'embauche", description: "Accords contractuels", excludable: true },
      { id: "A.6.3", title: "Sensibilisation, apprentissage et formation", description: "Programme de sensibilisation", excludable: true },
      { id: "A.6.4", title: "Processus disciplinaire", description: "Processus formel", excludable: true },
      { id: "A.6.5", title: "Responsabilités après la fin ou modification du contrat de travail", description: "Obligations post-emploi", excludable: true },
      { id: "A.6.6", title: "Engagements de confidentialité ou de non-divulgation", description: "Accords de confidentialité", excludable: true },
      { id: "A.6.7", title: "Travail à distance", description: "Mesures de sécurité télétravail", excludable: true },
      { id: "A.6.8", title: "Signalement des événements liés à la sécurité de l'information", description: "Mécanisme de signalement", excludable: true },
    ]
  },
  {
    id: "annexA7",
    title: "A.7 - Mesures physiques",
    icon: Lock,
    type: "control",
    items: [
      { id: "A.7.1", title: "Périmètres de sécurité physique", description: "Définition des zones sécurisées", excludable: true },
      { id: "A.7.2", title: "Contrôles physiques des accès", description: "Protection par contrôles d'entrée", excludable: true },
      { id: "A.7.3", title: "Sécurisation des bureaux, des salles et des équipements", description: "Sécurité physique des locaux", excludable: true },
      { id: "A.7.4", title: "Surveillance de la sécurité physique", description: "Surveillance continue", excludable: true },
      { id: "A.7.5", title: "Protection contre les menaces physiques et environnementales", description: "Protection environnementale", excludable: true },
      { id: "A.7.6", title: "Travail dans les zones sécurisées", description: "Mesures pour zones sensibles", excludable: true },
      { id: "A.7.7", title: "Bureau propre et écran vide", description: "Politique du bureau propre", excludable: true },
      { id: "A.7.8", title: "Emplacement et protection du matériel", description: "Protection du matériel", excludable: true },
      { id: "A.7.9", title: "Sécurité des actifs hors des locaux", description: "Protection hors site", excludable: true },
      { id: "A.7.10", title: "Supports de stockage", description: "Gestion des supports", excludable: true },
      { id: "A.7.11", title: "Services supports", description: "Protection des utilités", excludable: true },
      { id: "A.7.12", title: "Sécurité du câblage", description: "Protection des câbles", excludable: true },
      { id: "A.7.13", title: "Maintenance du matériel", description: "Maintenance appropriée", excludable: true },
      { id: "A.7.14", title: "Mise au rebut ou réutilisation sécurisée du matériel", description: "Élimination sécurisée", excludable: true },
    ]
  },
  {
    id: "annexA8",
    title: "A.8 - Mesures technologiques",
    icon: Cpu,
    type: "control",
    items: [
      { id: "A.8.1", title: "Terminaux utilisateurs finaux", description: "Protection des terminaux", excludable: true },
      { id: "A.8.2", title: "Droits d'accès privilégiés", description: "Restriction et gestion", excludable: true },
      { id: "A.8.3", title: "Restriction d'accès aux informations", description: "Accès selon la politique", excludable: true },
      { id: "A.8.4", title: "Accès au code source", description: "Restriction de l'accès", excludable: true },
      { id: "A.8.5", title: "Authentification sécurisée", description: "Techniques d'authentification", excludable: true },
      { id: "A.8.6", title: "Dimensionnement", description: "Surveillance des ressources", excludable: true },
      { id: "A.8.7", title: "Protection contre les programmes malveillants", description: "Détection et prévention", excludable: true },
      { id: "A.8.8", title: "Gestion des vulnérabilités techniques", description: "Identification et traitement", excludable: true },
      { id: "A.8.9", title: "Gestion des configurations", description: "Configurations sécurisées", excludable: true },
      { id: "A.8.10", title: "Suppression d'informations", description: "Effacement sécurisé", excludable: true },
      { id: "A.8.11", title: "Masquage des données", description: "Masquage selon la politique", excludable: true },
      { id: "A.8.12", title: "Prévention de la fuite de données", description: "Mesures DLP", excludable: true },
      { id: "A.8.13", title: "Sauvegarde des informations", description: "Copies de sauvegarde", excludable: true },
      { id: "A.8.14", title: "Redondance des moyens de traitement de l'information", description: "Haute disponibilité", excludable: true },
      { id: "A.8.15", title: "Journalisation", description: "Enregistrement des événements", excludable: true },
      { id: "A.8.16", title: "Activités de surveillance", description: "Surveillance des anomalies", excludable: true },
      { id: "A.8.17", title: "Synchronisation des horloges", description: "Horloges synchronisées", excludable: true },
      { id: "A.8.18", title: "Utilisation de programmes utilitaires privilégiés", description: "Restriction et contrôle", excludable: true },
      { id: "A.8.19", title: "Installation de logiciels sur des systèmes opérationnels", description: "Gestion des installations", excludable: true },
      { id: "A.8.20", title: "Sécurité des réseaux", description: "Protection des réseaux", excludable: true },
      { id: "A.8.21", title: "Sécurité des services réseau", description: "Mécanismes de sécurité", excludable: true },
      { id: "A.8.22", title: "Filtrage web", description: "Gestion de l'accès web", excludable: true },
      { id: "A.8.23", title: "Utilisation de la cryptographie", description: "Règles cryptographiques", excludable: true },
      { id: "A.8.24", title: "Cycle de vie de développement sécurisé", description: "Règles de développement", excludable: true },
      { id: "A.8.25", title: "Exigences de sécurité des applications", description: "Spécification des exigences", excludable: true },
      { id: "A.8.26", title: "Architecture d'application sécurisée", description: "Principes d'architecture", excludable: true },
      { id: "A.8.27", title: "Principes d'ingénierie et de codage sécurisés", description: "Techniques de codage sécurisé", excludable: true },
      { id: "A.8.28", title: "Tests de sécurité dans le développement et l'acceptation", description: "Tests de sécurité", excludable: true },
      { id: "A.8.29", title: "Développement externalisé", description: "Surveillance du développement externe", excludable: true },
      { id: "A.8.30", title: "Séparation des environnements de développement, de test et opérationnels", description: "Séparation des environnements", excludable: true },
      { id: "A.8.31", title: "Gestion des changements", description: "Procédures de gestion des changements", excludable: true },
      { id: "A.8.32", title: "Données de test", description: "Protection des données de test", excludable: true },
      { id: "A.8.33", title: "Protection des systèmes d'information pendant les tests d'audit", description: "Planification des tests d'audit", excludable: true },
      { id: "A.8.34", title: "Protection des systèmes d'information en cours d'audit", description: "Protection pendant l'audit", excludable: true },
    ]
  },
];

const STATUS_OPTIONS = [
  { value: "not_started", label: "Non démarré", color: "text-slate-400", bg: "bg-slate-500/20" },
  { value: "in_progress", label: "En cours", color: "text-cyber-volt", bg: "bg-cyber-volt/20" },
  { value: "implemented", label: "Implémenté", color: "text-blue-400", bg: "bg-blue-500/20" },
  { value: "verified", label: "Vérifié", color: "text-cyber-success", bg: "bg-cyber-success/20" },
  { value: "excluded", label: "Exclu (hors périmètre)", color: "text-orange-400", bg: "bg-orange-500/20" },
];

export default function MockExams() {
  const [projectData, setProjectData] = useState({
    name: "",
    scope: "",
    items: {},
    exclusions: {},
  });
  const [expandedSections, setExpandedSections] = useState({});
  const [loading, setLoading] = useState(true);
  const [editingItem, setEditingItem] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [projectSettingsOpen, setProjectSettingsOpen] = useState(false);

  useEffect(() => {
    fetchProjectData();
  }, []);

  const fetchProjectData = async () => {
    try {
      const response = await axios.get(`${API}/iso-project`);
      if (response.data && response.data.name) {
        setProjectData(response.data);
      }
    } catch (error) {
      console.log("No existing project, starting fresh");
    } finally {
      setLoading(false);
    }
  };

  const saveProjectData = async (data) => {
    try {
      await axios.post(`${API}/iso-project`, data);
      setProjectData(data);
      toast.success("Projet sauvegardé");
    } catch (error) {
      toast.error("Erreur lors de la sauvegarde");
    }
  };

  const updateItemStatus = (itemId, status, justification = "", evidence = "") => {
    const newData = {
      ...projectData,
      items: {
        ...projectData.items,
        [itemId]: { status, justification, evidence, updatedAt: new Date().toISOString() }
      }
    };
    if (status === "excluded") {
      newData.exclusions = {
        ...newData.exclusions,
        [itemId]: { justification, updatedAt: new Date().toISOString() }
      };
    } else if (projectData.exclusions?.[itemId]) {
      const { [itemId]: _, ...rest } = newData.exclusions;
      newData.exclusions = rest;
    }
    saveProjectData(newData);
  };

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  const getItemStatus = (itemId) => {
    return projectData.items?.[itemId]?.status || "not_started";
  };

  const getStatusConfig = (status) => {
    return STATUS_OPTIONS.find(s => s.value === status) || STATUS_OPTIONS[0];
  };

  const calculateStats = () => {
    const allItems = ISO_STRUCTURE.flatMap(section => section.items);
    const total = allItems.length;
    let verified = 0, implemented = 0, inProgress = 0, excluded = 0, notStarted = 0;
    
    allItems.forEach(item => {
      const status = getItemStatus(item.id);
      if (status === "verified") verified++;
      else if (status === "implemented") implemented++;
      else if (status === "in_progress") inProgress++;
      else if (status === "excluded") excluded++;
      else notStarted++;
    });

    const applicable = total - excluded;
    const compliant = verified + implemented;
    const percent = applicable > 0 ? Math.round((compliant / applicable) * 100) : 0;

    return { total, verified, implemented, inProgress, excluded, notStarted, applicable, compliant, percent };
  };

  const calculateSectionStats = (section) => {
    const total = section.items.length;
    let verified = 0, implemented = 0, excluded = 0;
    
    section.items.forEach(item => {
      const status = getItemStatus(item.id);
      if (status === "verified") verified++;
      else if (status === "implemented") implemented++;
      else if (status === "excluded") excluded++;
    });

    const applicable = total - excluded;
    const compliant = verified + implemented;
    const percent = applicable > 0 ? Math.round((compliant / applicable) * 100) : 0;

    return { total, verified, implemented, excluded, applicable, compliant, percent };
  };

  const openEditDialog = (item, section) => {
    setEditingItem({ 
      ...item, 
      sectionTitle: section.title,
      currentStatus: getItemStatus(item.id),
      currentJustification: projectData.items?.[item.id]?.justification || "",
      currentEvidence: projectData.items?.[item.id]?.evidence || ""
    });
    setEditDialogOpen(true);
  };

  const stats = calculateStats();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-cyber-primary font-mono">Chargement...</div>
      </div>
    );
  }

  // Show project setup if no project name
  if (!projectData.name) {
    return (
      <div className="max-w-2xl mx-auto" data-testid="project-setup">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="cyber-card p-8"
        >
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-cyber-primary/20 rounded-sm flex items-center justify-center mx-auto mb-4">
              <Target weight="duotone" className="w-8 h-8 text-cyber-primary" />
            </div>
            <h1 className="font-heading font-black text-2xl mb-2">Nouveau Projet ISO 27001</h1>
            <p className="text-slate-400">Configurez votre projet de certification</p>
          </div>

          <div className="space-y-6">
            <div>
              <label className="text-sm text-slate-300 mb-2 block">Nom du projet *</label>
              <Input
                value={projectData.name}
                onChange={(e) => setProjectData({ ...projectData, name: e.target.value })}
                placeholder="Ex: Certification ISO 27001 - E-Secure 2024"
                className="cyber-input"
                data-testid="project-name-input"
              />
            </div>

            <div>
              <label className="text-sm text-slate-300 mb-2 block">Périmètre du SMSI *</label>
              <Textarea
                value={projectData.scope}
                onChange={(e) => setProjectData({ ...projectData, scope: e.target.value })}
                placeholder="Décrivez le périmètre de votre système de management de la sécurité de l'information...&#10;&#10;Ex: Le SMSI couvre les activités de conseil en cybersécurité et les services managés de sécurité (SOC) fournis depuis nos locaux de Genève. Sont inclus: les systèmes d'information internes, les plateformes de service client, et les processus RH et administratifs associés."
                className="cyber-input min-h-[150px]"
                data-testid="project-scope-input"
              />
            </div>

            <Button
              onClick={() => saveProjectData({ ...projectData, items: {}, exclusions: {} })}
              disabled={!projectData.name || !projectData.scope}
              className="w-full bg-cyber-primary text-cyber-bg hover:bg-cyber-primary-hover"
              data-testid="create-project-btn"
            >
              Créer le projet
            </Button>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div data-testid="iso-project-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="font-heading font-black text-2xl md:text-3xl tracking-tight mb-1">
            {projectData.name}
          </h1>
          <p className="text-slate-400 text-sm">Projet de certification ISO 27001:2022</p>
        </div>
        <Button
          variant="outline"
          onClick={() => setProjectSettingsOpen(true)}
          className="border-cyber-border text-slate-300 hover:bg-cyber-surface-hover"
          data-testid="project-settings-btn"
        >
          <PencilSimple weight="duotone" className="w-4 h-4 mr-2" />
          Paramètres
        </Button>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="cyber-card p-4">
          <p className="text-xs text-slate-400 mb-1">Conformité</p>
          <p className={`font-mono text-2xl ${stats.percent >= 80 ? "text-cyber-success" : stats.percent >= 50 ? "text-cyber-volt" : "text-cyber-danger"}`}>
            {stats.percent}%
          </p>
        </motion.div>
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }} className="cyber-card p-4">
          <p className="text-xs text-slate-400 mb-1">Vérifiés</p>
          <p className="font-mono text-2xl text-cyber-success">{stats.verified}</p>
        </motion.div>
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="cyber-card p-4">
          <p className="text-xs text-slate-400 mb-1">Implémentés</p>
          <p className="font-mono text-2xl text-blue-400">{stats.implemented}</p>
        </motion.div>
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }} className="cyber-card p-4">
          <p className="text-xs text-slate-400 mb-1">En cours</p>
          <p className="font-mono text-2xl text-cyber-volt">{stats.inProgress}</p>
        </motion.div>
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="cyber-card p-4">
          <p className="text-xs text-slate-400 mb-1">Exclus</p>
          <p className="font-mono text-2xl text-orange-400">{stats.excluded}</p>
        </motion.div>
      </div>

      {/* Progress Bar */}
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="cyber-card p-4 mb-6">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm text-slate-400">{stats.compliant} / {stats.applicable} exigences applicables conformes</span>
          <span className="text-sm font-mono text-cyber-primary">{stats.percent}%</span>
        </div>
        <Progress value={stats.percent} className="h-2 bg-cyber-border" />
      </motion.div>

      {/* Sections */}
      <ScrollArea className="h-[calc(100vh-380px)]">
        <div className="space-y-2 pr-4">
          {ISO_STRUCTURE.map((section, sectionIndex) => {
            const sectionStats = calculateSectionStats(section);
            const isExpanded = expandedSections[section.id];
            const Icon = section.icon;

            return (
              <motion.div
                key={section.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: sectionIndex * 0.03 }}
                className="cyber-card overflow-hidden"
              >
                {/* Section Header */}
                <button
                  onClick={() => toggleSection(section.id)}
                  className="w-full p-4 flex items-center gap-3 hover:bg-cyber-surface-hover transition-colors"
                  data-testid={`section-${section.id}`}
                >
                  <div className={`w-9 h-9 rounded-sm flex items-center justify-center shrink-0 ${
                    sectionStats.percent >= 80 ? "bg-cyber-success/20" : 
                    sectionStats.percent >= 50 ? "bg-cyber-volt/20" : "bg-cyber-border"
                  }`}>
                    <Icon weight="duotone" className={`w-4 h-4 ${
                      sectionStats.percent >= 80 ? "text-cyber-success" : 
                      sectionStats.percent >= 50 ? "text-cyber-volt" : "text-slate-400"
                    }`} />
                  </div>
                  
                  <div className="flex-1 text-left min-w-0">
                    <h3 className="font-medium text-sm truncate">{section.title}</h3>
                    <p className="text-xs text-slate-500">
                      {sectionStats.compliant}/{sectionStats.applicable} conformes
                      {sectionStats.excluded > 0 && ` • ${sectionStats.excluded} exclus`}
                    </p>
                  </div>

                  <div className="flex items-center gap-2">
                    <span className={`font-mono text-sm ${
                      sectionStats.percent >= 80 ? "text-cyber-success" : 
                      sectionStats.percent >= 50 ? "text-cyber-volt" : "text-slate-400"
                    }`}>
                      {sectionStats.percent}%
                    </span>
                    {isExpanded ? (
                      <CaretDown weight="bold" className="w-4 h-4 text-slate-400" />
                    ) : (
                      <CaretRight weight="bold" className="w-4 h-4 text-slate-400" />
                    )}
                  </div>
                </button>

                {/* Section Items */}
                <AnimatePresence>
                  {isExpanded && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      className="border-t border-cyber-border"
                    >
                      {section.items.map((item) => {
                        const status = getItemStatus(item.id);
                        const statusConfig = getStatusConfig(status);
                        const itemData = projectData.items?.[item.id];

                        return (
                          <div
                            key={item.id}
                            className="flex items-start gap-3 p-3 hover:bg-cyber-surface-hover transition-colors border-b border-cyber-border last:border-0 cursor-pointer"
                            onClick={() => openEditDialog(item, section)}
                            data-testid={`item-${item.id}`}
                          >
                            <div className={`w-6 h-6 rounded-sm flex items-center justify-center shrink-0 mt-0.5 ${statusConfig.bg}`}>
                              {status === "verified" && <CheckCircle weight="fill" className="w-4 h-4 text-cyber-success" />}
                              {status === "implemented" && <CheckCircle weight="fill" className="w-4 h-4 text-blue-400" />}
                              {status === "in_progress" && <Warning weight="fill" className="w-4 h-4 text-cyber-volt" />}
                              {status === "excluded" && <Prohibit weight="fill" className="w-4 h-4 text-orange-400" />}
                              {status === "not_started" && <div className="w-2 h-2 rounded-full bg-slate-500" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className={`text-sm ${status === "excluded" ? "text-slate-500 line-through" : ""}`}>
                                <span className="text-cyber-primary font-mono mr-2">{item.id}</span>
                                {item.title}
                              </p>
                              <p className="text-xs text-slate-500 mt-0.5">{item.description}</p>
                              {itemData?.justification && status === "excluded" && (
                                <p className="text-xs text-orange-400/70 mt-1 italic">
                                  Justification: {itemData.justification}
                                </p>
                              )}
                            </div>
                            {item.excludable && (
                              <span className="text-[10px] text-slate-500 bg-cyber-border px-1.5 py-0.5 rounded shrink-0">
                                Excluable
                              </span>
                            )}
                          </div>
                        );
                      })}
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>
      </ScrollArea>

      {/* Edit Item Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="bg-cyber-surface border-cyber-border max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-lg">
              <span className="text-cyber-primary font-mono mr-2">{editingItem?.id}</span>
              {editingItem?.title}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <p className="text-sm text-slate-400">{editingItem?.description}</p>
            
            <div>
              <label className="text-sm text-slate-300 mb-2 block">Statut</label>
              <Select
                value={editingItem?.currentStatus}
                onValueChange={(value) => setEditingItem({ ...editingItem, currentStatus: value })}
              >
                <SelectTrigger className="cyber-input">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.filter(s => s.value !== "excluded" || editingItem?.excludable).map(status => (
                    <SelectItem key={status.value} value={status.value}>
                      <span className={status.color}>{status.label}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {editingItem?.currentStatus === "excluded" && (
              <div>
                <label className="text-sm text-slate-300 mb-2 block">
                  Justification de l'exclusion *
                </label>
                <Textarea
                  value={editingItem?.currentJustification}
                  onChange={(e) => setEditingItem({ ...editingItem, currentJustification: e.target.value })}
                  placeholder="Expliquez pourquoi ce contrôle est hors périmètre..."
                  className="cyber-input min-h-[80px]"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Cette justification sera incluse dans la Déclaration d'Applicabilité (DdA)
                </p>
              </div>
            )}

            {editingItem?.currentStatus !== "excluded" && editingItem?.currentStatus !== "not_started" && (
              <div>
                <label className="text-sm text-slate-300 mb-2 block">Preuves / Références</label>
                <Textarea
                  value={editingItem?.currentEvidence}
                  onChange={(e) => setEditingItem({ ...editingItem, currentEvidence: e.target.value })}
                  placeholder="Documents, procédures, captures d'écran..."
                  className="cyber-input min-h-[60px]"
                />
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setEditDialogOpen(false)}
              className="text-slate-400"
            >
              Annuler
            </Button>
            <Button
              onClick={() => {
                updateItemStatus(
                  editingItem.id, 
                  editingItem.currentStatus, 
                  editingItem.currentJustification,
                  editingItem.currentEvidence
                );
                setEditDialogOpen(false);
              }}
              disabled={editingItem?.currentStatus === "excluded" && !editingItem?.currentJustification}
              className="bg-cyber-primary text-cyber-bg hover:bg-cyber-primary-hover"
            >
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Project Settings Dialog */}
      <Dialog open={projectSettingsOpen} onOpenChange={setProjectSettingsOpen}>
        <DialogContent className="bg-cyber-surface border-cyber-border max-w-lg">
          <DialogHeader>
            <DialogTitle>Paramètres du projet</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm text-slate-300 mb-2 block">Nom du projet</label>
              <Input
                value={projectData.name}
                onChange={(e) => setProjectData({ ...projectData, name: e.target.value })}
                className="cyber-input"
              />
            </div>

            <div>
              <label className="text-sm text-slate-300 mb-2 block">Périmètre du SMSI</label>
              <Textarea
                value={projectData.scope}
                onChange={(e) => setProjectData({ ...projectData, scope: e.target.value })}
                className="cyber-input min-h-[120px]"
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setProjectSettingsOpen(false)}
              className="text-slate-400"
            >
              Annuler
            </Button>
            <Button
              onClick={() => {
                saveProjectData(projectData);
                setProjectSettingsOpen(false);
              }}
              className="bg-cyber-primary text-cyber-bg hover:bg-cyber-primary-hover"
            >
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
